import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Navbar from "./navBar";
import Footer from "./footer";
import { EmployeeAppApiBase } from "./BaseUrl";

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loader, setLoader] = useState(false);
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const emailFromState = location.state?.email || "";

  useEffect(() => {
    if (!emailFromState) {
      navigate("/forgot-password", { replace: true });
    }
  }, [emailFromState, navigate]);

  const formik = useFormik({
    initialValues: {
      email: emailFromState,
      otp: "",
      newPassword: "",
      confirmPassword: "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email").required("Required"),
      otp: Yup.string().trim().required("OTP is required"),
      newPassword: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
        .required("Required"),
    }),
    onSubmit: async (values) => {
      setLoader(true);
      setMessage("");
      try {
        const response = await fetch(`${EmployeeAppApiBase}/reset-password-with-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: values.email.trim().toLowerCase(),
            otp: values.otp.trim(),
            newPassword: values.newPassword.trim(),
          }),
        });
        const data = await response.json();

        if (data.success) {
          setMessage(data.msg || "Password reset successfully.");
          setTimeout(() => navigate("/login/"), 2000);
        } else {
          setMessage(data.msg || "Invalid or expired OTP. Please try again.");
          setLoader(false);
        }
      } catch (error) {
        console.error("ResetPassword error:", error);
        setMessage("An error occurred. Please try again later.");
        setLoader(false);
      }
    },
  });

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  if (!emailFromState) return null;

  return (
    <>
      <Navbar />
      <div className="border border-green-700"></div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row mb-14 justify-center mt-14 gap-14">
          <div className="lg:w-[400px] mx-auto">
            <div className="font-bold text-center text-2xl text-green-600">
              Reset Password
            </div>
            <p className="text-center text-slate-600 mt-2 text-sm">
              Enter the OTP sent to your email and your new password.
            </p>
            <div className="text-red-600 text-center mt-4">{message}</div>
            <form onSubmit={formik.handleSubmit} className="mt-6">
              <input
                type="email"
                placeholder="Email Address"
                className="border-b w-full mt-4 p-2 bg-slate-50"
                readOnly
                {...formik.getFieldProps("email")}
              />
              {formik.touched.email && formik.errors.email ? (
                <div className="text-red-500 text-sm mt-1">{formik.errors.email}</div>
              ) : null}

              <input
                type="text"
                placeholder="Enter OTP"
                className="border-b w-full mt-6 p-2"
                maxLength={6}
                {...formik.getFieldProps("otp")}
              />
              {formik.touched.otp && formik.errors.otp ? (
                <div className="text-red-500 text-sm mt-1">{formik.errors.otp}</div>
              ) : null}

              <div className="flex items-center mt-6">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="New Password (min 6 characters)"
                  className="border-b w-full p-2"
                  {...formik.getFieldProps("newPassword")}
                />
                <span
                  onClick={togglePasswordVisibility}
                  className="toggle-password -ml-10 cursor-pointer"
                  style={{ cursor: "pointer" }}
                >
                  {showPassword ? "👁️" : "🙈"}
                </span>
              </div>
              {formik.touched.newPassword && formik.errors.newPassword ? (
                <div className="text-red-500 text-sm mt-1">{formik.errors.newPassword}</div>
              ) : null}

              <div className="flex items-center mt-6">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm New Password"
                  className="border-b w-full p-2"
                  {...formik.getFieldProps("confirmPassword")}
                />
              </div>
              {formik.touched.confirmPassword && formik.errors.confirmPassword ? (
                <div className="text-red-500 text-sm mt-1">{formik.errors.confirmPassword}</div>
              ) : null}

              <button
                type="submit"
                disabled={loader}
                className="bg-[#108A00] hover:bg-green-800 p-2 w-full text-white rounded-md text-center mt-10 flex justify-center items-center cursor-pointer disabled:opacity-70"
              >
                {loader ? (
                  <svg
                    className="h-5 w-5 animate-spin"
                    fill="white"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                  >
                    <path d="M304 48a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zm0 416a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zM48 304a48 48 0 1 0 0-96 48 48 0 1 0 0 96zm464-48a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zM142.9 437A48 48 0 1 0 75 369.1 48 48 0 1 0 142.9 437zm0-294.2A48 48 0 1 0 75 75a48 48 0 1 0 67.9 67.9zM369.1 437A48 48 0 1 0 437 369.1 48 48 0 1 0 369.1 437z" />
                  </svg>
                ) : (
                  "Reset Password"
                )}
              </button>
            </form>
            <div className="flex items-center justify-center mt-6 gap-2">
              <span className="text-slate-400">Didn&apos;t receive OTP?</span>
              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="text-[#108A00] font-medium cursor-pointer"
              >
                Request again
              </button>
            </div>
            <div className="flex items-center justify-center mt-4 gap-2">
              <span className="text-slate-400">Back to</span>
              <button
                type="button"
                onClick={() => navigate("/login/")}
                className="text-[#108A00] font-medium cursor-pointer"
              >
                Login
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
